import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { useLeads } from '@/features/leads/hooks/useLeads';
import { leadsApi } from '@/features/leads/api/leadsApi';
import { LeadListItem } from '@/features/leads/components/LeadListItem';
import { PendingLeadItem } from '@/features/leads/components/PendingLeadItem';
import { pendingScanRepository } from '@/db/repositories/pendingScanRepository';
import { useLiveQuery } from 'dexie-react-hooks';
import { EmptyState } from '@/shared/components/EmptyState';
import { Spinner } from '@/shared/components/Spinner';
import { ExportButton } from '@/shared/components/ExportButton';
import { ApiError } from '@/shared/api/httpClient';
import { useDebounce } from '@/shared/hooks/useDebounce';

export function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { leads, isLoading, error, refetch } = useLeads(user?.exhibitorId ?? undefined);
  const pendingScans = useLiveQuery(() => pendingScanRepository.getPending()) ?? [];

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);

  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExportMine(format: 'csv' | 'xlsx') {
    setExportError(null);
    try {
      await (format === 'csv' ? leadsApi.exportMineCsv() : leadsApi.exportMineXlsx());
    } catch (err) {
      setExportError(err instanceof ApiError ? err.userMessage : `Could not export ${format.toUpperCase()}.`);
    }
  }

  const filteredLeads = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return leads;

    return leads.filter((lead) =>
      [lead.firstName, lead.lastName, lead.company, lead.badgeId]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(term))
    );
  }, [leads, debouncedSearch]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-7)' }}>
        <Spinner label="Loading leads" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="⚠"
        title="Couldn't load leads"
        description={error}
        action={
          <button type="button" className="btn btn--secondary" onClick={refetch}>
            Try again
          </button>
        }
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <ExportButton onExport={handleExportMine} />
      </div>

      {exportError && <div className="error-banner" style={{ marginBottom: 'var(--space-4)' }}>{exportError}</div>}

      {leads.length === 0 && pendingScans.length === 0 ? (
        <EmptyState
          icon="☰"
          title="No leads yet"
          description="Scanned badges will appear here as soon as you capture your first lead."
        />
      ) : (
        <>
          <input
            className="field__input"
            style={{ marginBottom: 'var(--space-4)' }}
            placeholder="Search by name, company, or badge ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search leads"
          />

          {pendingScans.map((scan) => (
            <PendingLeadItem key={scan.localId} scan={scan} />
          ))}

          {filteredLeads.map((lead) => (
            <LeadListItem key={lead.scanId} lead={lead} onClick={() => navigate(`/leads/${lead.scanId}`, { state: { lead } })} />
          ))}
        </>
      )}
    </div>
  );
}